import { useCallback, useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../config/api'

// NOT upload.middleware's 50MB — that limit is sized for video. Anthropic
// rejects a single image over 10MB base64-encoded, and base64 adds about a
// third, so anything over ~7.5MB on disk fails after a full upload with a
// message that blames the flyer. Mirrors MAX_FLYER_BYTES in
// flyerExtraction.service.js, which is the enforcing check.
const MAX_FLYER_MB = 7
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf']

/**
 * Two-step flyer update: extract a diff, then apply the accepted subset.
 *
 * The preview call writes nothing, which is the whole point — a re-uploaded
 * flyer usually repeats most of the event, and an unreviewed overwrite would
 * clobber hand-corrected fields and drop artists the AI misread.
 */
export const useFlyerUpdate = (eventId, onApplied) => {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [changes, setChanges] = useState(null)
  const [flyerImage, setFlyerImage] = useState(null)
  const [accepted, setAccepted] = useState(new Set())
  const [extracting, setExtracting] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')

  const reset = useCallback(() => {
    setFile(null)
    setPreview(null)
    setChanges(null)
    setFlyerImage(null)
    setAccepted(new Set())
    setExtracting(false)
    setApplying(false)
    setError('')
  }, [])

  const selectFile = useCallback((selected) => {
    if (!selected) return

    const extension = selected.name.slice(selected.name.lastIndexOf('.')).toLowerCase()
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setError('Flyers must be a JPG, PNG, or PDF file')
      return
    }
    if (selected.size > MAX_FLYER_MB * 1024 * 1024) {
      setError(`That file is too large (max ${MAX_FLYER_MB}MB)`)
      return
    }

    setError('')
    setFile(selected)
    setChanges(null)
    setAccepted(new Set())

    // PDFs have no inline preview; the filename stands in for one.
    if (selected.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => setPreview(e.target.result)
      reader.readAsDataURL(selected)
    } else {
      setPreview(null)
    }
  }, [])

  /** Uploads the flyer and returns the change list. Nothing is written yet. */
  const extract = useCallback(async () => {
    if (!file) return

    setExtracting(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('flyer', file)

      const response = await axios.post(
        `${API_BASE_URL}/api/events/${eventId}/flyer-update`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      )

      const detected = response.data.changes || []
      setChanges(detected)
      setFlyerImage(response.data.flyerImage || null)

      // Everything is pre-accepted EXCEPT removals. Dropping an artist is the
      // one irreversible change here, and a crowded flyer the AI read only
      // partially would otherwise propose deleting most of the lineup with
      // every box already ticked.
      setAccepted(new Set(
        detected.filter(change => change.type !== 'artist-removed').map(change => change.id)
      ))
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Flyer extraction failed:', err)
      if (err.response?.status === 429) {
        setError('Too many AI requests right now. Try again a bit later.')
      } else {
        setError(err.response?.data?.message || 'Could not read that flyer')
      }
    } finally {
      setExtracting(false)
    }
  }, [eventId, file])

  const toggleChange = useCallback((changeId) => {
    setAccepted(current => {
      const next = new Set(current)
      if (next.has(changeId)) next.delete(changeId)
      else next.add(changeId)
      return next
    })
  }, [])

  const setAllAccepted = useCallback((value) => {
    setAccepted(value && changes ? new Set(changes.map(change => change.id)) : new Set())
  }, [changes])

  /** Commits the accepted subset. */
  const apply = useCallback(async () => {
    if (!changes || accepted.size === 0) return

    setApplying(true)
    setError('')

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/events/${eventId}/flyer-update/apply`,
        {
          changes,
          acceptedIds: Array.from(accepted),
          flyerImage
        },
        { withCredentials: true }
      )

      if (onApplied) onApplied(response.data)
      reset()
      return true
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Flyer apply failed:', err)
      setError(err.response?.data?.message || 'Failed to apply changes')
      return false
    } finally {
      setApplying(false)
    }
  }, [eventId, changes, accepted, flyerImage, onApplied, reset])

  return {
    file,
    preview,
    changes,
    accepted,
    extracting,
    applying,
    error,
    selectFile,
    extract,
    toggleChange,
    setAllAccepted,
    apply,
    reset
  }
}
